import { useState, useRef, useCallback, useEffect } from "react";
import type {
  Props as SelectProps,
  GroupBase,
  StylesConfig,
} from "react-select";

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

interface UsePortalDropdownOptions {
  offset?: number; // Gap between trigger and dropdown (default: 4px)
}

type ReactSelectProps = Pick<
  SelectProps<unknown, boolean, GroupBase<unknown>>,
  | "menuPortalTarget"
  | "menuPosition"
  | "menuShouldScrollIntoView"
  | "menuShouldBlockScroll"
  | "menuIsOpen"
  | "onMenuOpen"
  | "onMenuClose"
>;

interface UsePortalDropdownReturn {
  isOpen: boolean;
  position: DropdownPosition;
  triggerRef: React.RefObject<HTMLElement | null>;
  dropdownRef: React.RefObject<HTMLElement | null>;
  open: () => void;
  close: () => void;
  toggle: (e?: React.MouseEvent) => void;
  // React-select specific utilities
  getReactSelectProps: () => ReactSelectProps;
  getReactSelectStyles: <
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    baseStyles: StylesConfig<Option, IsMulti, Group>,
  ) => StylesConfig<Option, IsMulti, Group>;
}

/**
 * Reusable hook for portal-based dropdowns that escape modal constraints
 * Handles positioning, resize listeners, and click-outside detection
 * Includes react-select integration utilities
 */
export const usePortalDropdown = (
  options: UsePortalDropdownOptions = { offset: 4 },
): UsePortalDropdownReturn => {
  const { offset = 4 } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
  });
  const triggerRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLElement>(null);

  // Function to calculate dropdown position
  const calculatePosition = useCallback((): DropdownPosition => {
    if (!triggerRef.current) {
      return { top: 0, left: 0, width: 0 };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + offset, // Use viewport coordinates for fixed positioning
      left: rect.left, // Use viewport coordinates for fixed positioning
      width: rect.width,
    };
  }, [offset]);

  // Function to recalculate and update position
  const updatePosition = useCallback(() => {
    if (isOpen) {
      setPosition(calculatePosition());
    }
  }, [isOpen, calculatePosition]);

  // Open dropdown
  const open = useCallback(() => {
    const newPosition = calculatePosition();
    setPosition(newPosition);
    setIsOpen(true);
  }, [calculatePosition]);

  // Close dropdown
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Toggle dropdown
  const toggle = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isOpen) {
        close();
      } else {
        open();
      }
    },
    [isOpen, open, close],
  );

  // React-select props generator
  const getReactSelectProps = useCallback(
    (): ReactSelectProps => ({
      menuPortalTarget: document.body,
      menuPosition: "fixed" as const,
      menuShouldScrollIntoView: false,
      menuShouldBlockScroll: false,
      menuIsOpen: isOpen,
      onMenuOpen: open,
      onMenuClose: close,
    }),
    [isOpen, open, close],
  );

  // React-select styles generator

  const getReactSelectStyles = useCallback(
    <
      Option,
      IsMulti extends boolean,
      Group extends GroupBase<Option> = GroupBase<Option>,
    >(
      baseStyles: StylesConfig<Option, IsMulti, Group>,
    ): StylesConfig<Option, IsMulti, Group> => ({
      ...baseStyles,
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
        // Use hook's calculated position when menu is open
        ...(isOpen && {
          position: "fixed" as const,
          top: position.top,
          left: position.left,
          width: position.width,
        }),
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        // Override positioning when using portal
        ...(isOpen && {
          position: "static" as const,
          transform: "none",
          marginTop: 0,
          marginBottom: 0,
        }),
      }),
    }),
    [isOpen, position],
  );

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    if (isOpen) {
      const handlePositionUpdate = () => {
        updatePosition();
      };

      // Find all scrollable ancestors of the trigger element
      const getScrollableAncestors = (element: HTMLElement): HTMLElement[] => {
        const ancestors: HTMLElement[] = [];
        let current = element.parentElement;

        while (current && current !== document.body) {
          const style = window.getComputedStyle(current);
          const isScrollable =
            ["auto", "scroll"].includes(style.overflow) ||
            ["auto", "scroll"].includes(style.overflowY) ||
            ["auto", "scroll"].includes(style.overflowX);

          if (isScrollable) {
            ancestors.push(current);
          }
          current = current.parentElement;
        }

        return ancestors;
      };

      // Add listeners to window and all scrollable ancestors
      const scrollableElements = triggerRef.current
        ? [window as any, ...getScrollableAncestors(triggerRef.current)]
        : [window as any];

      // Use passive listeners for better performance
      scrollableElements.forEach((element) => {
        if (element === window) {
          window.addEventListener("resize", handlePositionUpdate, {
            passive: true,
          });
          window.addEventListener("scroll", handlePositionUpdate, {
            passive: true,
          });
        } else {
          element.addEventListener("scroll", handlePositionUpdate, {
            passive: true,
          });
        }
      });

      return () => {
        scrollableElements.forEach((element: HTMLElement | Window) => {
          if (element === window) {
            window.removeEventListener("resize", handlePositionUpdate);
            window.removeEventListener("scroll", handlePositionUpdate);
          } else {
            element.removeEventListener("scroll", handlePositionUpdate);
          }
        });
      };
    }
  }, [isOpen, updatePosition]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, close]);

  return {
    isOpen,
    position,
    triggerRef,
    dropdownRef,
    open,
    close,
    toggle,
    getReactSelectProps,
    getReactSelectStyles,
  };
};
